import { type MedplumClient, formatHumanName } from '@medplum/core';
import type {
  Bundle,
  Practitioner,
  Resource,
  ServiceRequest,
} from '@medplum/fhirtypes';

import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import {
  REVIEW_STATUS_ATTESTED,
  REVIEW_STATUS_REJECTED,
  isPendingReview,
  setReviewStatusTag,
} from '../review-markers';

import { FHIR_PATIENT_ID_QUERY, PATIENT_TODOS_QUERY } from './fhirQueries';

/**
 * Data layer for the Patient to-dos card: reads via the FHIR GraphQL
 * connection, review writes via the Medplum SDK client. The two paths share
 * no cache — callers refetch the read after a write lands.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

/**
 * Flatten the connection payload into ServiceRequests, backfilling each
 * requester reference's `display` from the inline-resolved Practitioner
 * (clinician-added action items carry none; the flatten helper strips the
 * nested resource into the collected list).
 */
export function mapTodos(
  data: ResultOf<typeof PATIENT_TODOS_QUERY>,
): ServiceRequest[] {
  const resources = flattenFhirGraphQLData(data);
  const names = practitionerNamesByRef(resources);
  return resources
    .filter(
      (resource): resource is ServiceRequest & { id: string } =>
        resource.resourceType === 'ServiceRequest' && Boolean(resource.id),
    )
    .map((todo) => {
      const requesterRef = todo.requester?.reference;
      if (!todo.requester || todo.requester.display || !requesterRef) {
        return todo;
      }
      const display = names.get(requesterRef);
      return display
        ? { ...todo, requester: { ...todo.requester, display } }
        : todo;
    });
}

function practitionerNamesByRef(resources: Resource[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const resource of resources) {
    if (resource.resourceType !== 'Practitioner' || !resource.id) continue;
    const name = (resource as Practitioner).name?.[0];
    if (name) names.set(`Practitioner/${resource.id}`, formatHumanName(name));
  }
  return names;
}

/** The patient's to-dos, paged newest-first. Skipped until the FHIR id resolves. */
export function usePatientTodos(
  fhirPatientId: string | null,
): FhirConnectionState<ServiceRequest[]> {
  return useFhirConnection(
    PATIENT_TODOS_QUERY,
    'ServiceRequestConnection',
    mapTodos,
    {
      variables: { patientRef: `Patient/${fhirPatientId}` },
      skip: !fhirPatientId,
    },
  );
}

export type TodoReviewDecision = 'accept' | 'dismiss';

/**
 * Apply a reviewer's decision to a pending AI-proposed to-do — the same
 * transition the backend care-plan review applies to an action item
 * (`care-plan-review.service.ts` decideItem): re-read, verify still pending,
 * swap the review-status tag (accept → attested, dismiss → rejected). The
 * native status is deliberately untouched and no Provenance is written —
 * dismissed proposals are hidden by the tag, not revoked. ifMatch pins the
 * write to the version the pending check saw, so a concurrent reviewer's
 * decision is surfaced instead of silently overwritten.
 */
export async function reviewServiceRequest(
  client: MedplumClient,
  args: {
    serviceRequestId: string;
    decision: TodoReviewDecision;
  },
): Promise<void> {
  const { serviceRequestId, decision } = args;
  const current = await client.readResource('ServiceRequest', serviceRequestId);
  if (!isPendingReview(current)) {
    throw new Error('This to-do is no longer awaiting review.');
  }

  const next = setReviewStatusTag(
    current,
    decision === 'dismiss' ? REVIEW_STATUS_REJECTED : REVIEW_STATUS_ATTESTED,
  );

  const response = await client.executeBatch({
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      {
        request: {
          method: 'PUT',
          url: `ServiceRequest/${serviceRequestId}`,
          ...(current.meta?.versionId
            ? { ifMatch: `W/"${current.meta.versionId}"` }
            : {}),
        },
        resource: next,
      },
    ],
  });
  assertNoFailedEntries(response);
}

function assertNoFailedEntries(response: Bundle): void {
  const statuses = (response.entry ?? [])
    .map((entry) => Number.parseInt(entry.response?.status ?? '', 10))
    .filter((code) => !(code >= 200 && code < 300));
  if (statuses.length === 0) return;
  if (statuses.some((code) => code === 409 || code === 412)) {
    throw new Error('This to-do has already been reviewed by someone else.');
  }
  throw new Error(`Review write failed: ${statuses.join(', ')}`);
}
